import { EntityKind } from "models/entityKind";
import { BIN_TYPES, IGroupBy } from "models/groupBy";

/**
 * Quantile boundaries are computed from the well population, so any change to
 * the filtered set moves the numbers the legend shows. Every other bin type
 * either has fixed edges or is meant to re-derive, and only the boundaries need
 * protecting — counts are expected to follow the wells.
 *
 * Dynamic bins are also population-derived, and are deliberately *not* covered.
 * The two are asked for different things: a quantile legend is a fixed set of
 * splits (quartiles, say) whose whole value is being exact for the population
 * on screen, so a re-derive mid-composition silently invalidates it. Dynamic
 * bins exist to follow the population — the user expects them to move as the
 * map does, and freezing them would take away the behavior they were chosen
 * for. The same goes for a max-bins cap: it is a live ranking, not a fixed
 * split — and for the bubble-vis legend's min/max, which is the range of the
 * population drawn: it is rebuilt from the same payload that sizes the bubbles,
 * so legend and map move together, and freezing it would pin the expanded map
 * to the population it had before it expanded.
 *
 * Under group mode the bin settings live on `mode.bin` rather than the
 * top-level `bin`, and either one can carry the quantile choice, so both are
 * checked: testing only the top level lets group-mode quantile re-bin unnoticed.
 */
export const hasQuantileBinning = (groupBy: Maybe<IGroupBy>): boolean =>
  groupBy?.bin?.BinType === BIN_TYPES.Quantile ||
  groupBy?.mode?.bin?.BinType === BIN_TYPES.Quantile;

export interface QuantileBinningQuery {
  readonly activeEntityKinds: Maybe<readonly EntityKind[]>;
  readonly wellGroupBy: Maybe<IGroupBy>;
  readonly facilityGroupBy: Maybe<IGroupBy>;
}

/**
 * Wells and facilities keep separate group-by trees, and one viewport filter
 * serves both — so a quantile legend on either active kind is enough to need
 * protecting.
 */
export const isQuantileBinningActive = ({
  activeEntityKinds,
  wellGroupBy,
  facilityGroupBy
}: QuantileBinningQuery): boolean =>
  // Read straight out of session storage, so a stored "null" arrives as null;
  // this runs on every MapboxWrapper render and must not throw there.
  (activeEntityKinds ?? []).some((entityKind) =>
    hasQuantileBinning(entityKind === EntityKind.Well ? wellGroupBy : facilityGroupBy)
  );
